import React, { CSSProperties, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import useDidMountEffect from "../hooks/useDidMountEffect";
import { FlexContainer, StyledFlexContainerProps } from "../Containers";
import { ColorVariables } from "../Colors";
import { baseLength, getMobileBreakpoint } from "../Styles/globals";

// Taken from: https://loading.io/css/
const StyledSpinnerContainer = styled.span<{ $size: number; $spinnerColor: string }>`
  display: block;
  position: relative;
  width: ${(props) => `${props.$size}px`};
  height: ${(props) => `${props.$size}px`};
  span {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: ${(props) => `${0.8 * props.$size}px`};
    height: ${(props) => `${0.8 * props.$size}px`};
    margin: ${(props) => `${props.$size / 10}px`};
    border: ${(props) => `${props.$size / 10}px solid #fff`};
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: ${(props) => props.$spinnerColor} transparent transparent transparent;
  }
  span:nth-child(1) {
    animation-delay: -0.45s;
  }
  span:nth-child(2) {
    animation-delay: -0.3s;
  }
  span:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Spinner = (props: { size: number; className?: string; $spinnerColor: string }) => {
  return (
    <StyledSpinnerContainer $spinnerColor={props.$spinnerColor} $size={props.size} className={props.className}>
      <span />
      <span />
      <span />
      <span />
    </StyledSpinnerContainer>
  );
};

export const ButtonLink = styled.button<{ $center?: boolean }>`
  border-bottom: 1px solid transparent;
  display: inline-block;
  text-align: ${(props) => (props.$center ? "center" : "left")};
  width: auto;
  font-weight: 400;
  color: ${ColorVariables.mint};
  &:hover {
    opacity: 0.8;
    text-decoration: underline;
    text-decoration-color: ${ColorVariables.mint};
  }
`;

export type ButtonProps = {
  name: string;
  text?: string | JSX.Element;
  onClick?: (...args: any) => any;
  backgroundColor?: string;
  inverted?: boolean;
  disabled?: boolean;
  className?: string;
  loading?: boolean;
  type?: "button" | "submit";
  style?: CSSProperties;
  plain?: boolean;
};

const StyledButton = styled(FlexContainer)<
  StyledFlexContainerProps &
    Partial<{ $inverted: boolean; $disabled: boolean; $plain: boolean; $backgroundColor: string }>
>`
  transition: opacity 0.05s ease-out;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome and Opera */
  border-radius: ${baseLength * 2.5}px;

  background-color: ${(props) => {
    if (props.$inverted && props.$disabled) {
      return ColorVariables.coco3;
    }
    if (props.$inverted || props.$plain) {
      return "inherit";
    }
    if (props.$disabled) {
      return ColorVariables.coco1;
    }
    return props.$backgroundColor;
  }};

  border: solid ${baseLength * 0.5}px
    ${(props) => {
      if (props.$inverted && props.$disabled) {
        return ColorVariables.coco3;
      }
      if (props.$disabled || props.$plain) {
        return "none";
      }
      return props.$backgroundColor;
    }};
  p {
    color: ${(props) => {
      if (props.$inverted && props.$disabled) {
        return ColorVariables.coco3;
      }
      if (props.$disabled) {
        return ColorVariables.coco5;
      }
      if (props.$inverted || props.$plain) {
        return ColorVariables.mint;
      }
      return ColorVariables.sugar;
    }};
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }

  &:not([disabled])&:active {
    opacity: 0.9;
  }

  @media (max-width: ${getMobileBreakpoint}) {
    max-width: 100%;
  }
`;

const StyledButtonContainer = styled.button`
  position: relative;
  .spinnerContainer {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
  }
  .spinner {
    margin: auto;
    transform: translateX(-200px);
    opacity: 0;
  }
  &.loading-init {
    .spinnerContainer {
      background: rgba(255, 255, 255, 0.3);
    }
  }
  &.loading {
    .spinnerContainer {
      background: rgba(255, 255, 255, 0.3);
    }
    .text {
      animation: move-out 0.2s cubic-bezier(0.5, 0, 0.5, 1);
      animation-fill-mode: forwards;
    }
    .spinner {
      animation: move-in 0.1s cubic-bezier(0.5, 0, 0.5, 1);
      animation-fill-mode: forwards;
    }
  }
  &.loading-exit {
    .text {
      animation: move-in 0.2s cubic-bezier(0.5, 0, 0.5, 1);
      animation-fill-mode: forwards;
    }
    .spinner {
      animation: move-out 0.3s cubic-bezier(0.5, 0, 0.5, 1);
      animation-fill-mode: forwards;
    }
  }
  &:disabled {
    background-color: ${ColorVariables.cream};
  }

  @keyframes move-in {
    0% {
      transform: translateX(-200px);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
  @keyframes move-out {
    0% {
      transform: translateX(0);
      opacity: 1;
    }
    100% {
      transform: translateX(-200px);
      opacity: 0;
    }
  }
`;

const Button = ({
  name,
  text = "",
  onClick,
  backgroundColor = ColorVariables.mint,
  inverted = false,
  plain = false,
  disabled = false,
  className = "",
  type = "button",
  loading = false,
  style,
}: ButtonProps) => {
  const loadingRef = useRef(false);
  const [loadingClassName, setLoadingClassName] = useState("");
  const canClick = !loading && !disabled;

  useDidMountEffect(() => {
    if (loading) {
      setLoadingClassName("loading-init");
      setTimeout(() => {
        setLoadingClassName(loadingRef.current ? "loading" : "");
      }, 500);
    } else if (loadingClassName === "loading-init") {
      setLoadingClassName("");
    } else if (loadingClassName === "loading") {
      setLoadingClassName("loading-exit");
    }
  }, [loading]);

  useDidMountEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const spinnerColor = useMemo(() => {
    if (inverted) return ColorVariables.mint;
    return "#fff";
  }, [inverted]);

  return (
    <StyledButtonContainer
      data-name={name}
      disabled={disabled || loading}
      className={`button ${className} ${loadingClassName}`}
      type={type}
      onClick={canClick ? onClick : undefined}
      style={style}
    >
      <StyledButton
        style={{
          height: baseLength * 10,
          cursor: !disabled ? "pointer" : undefined,
        }}
        $mobileHeight={baseLength * 8.75}
        $justifyContent="center"
        $alignItems="center"
        $inverted={inverted}
        $plain={plain}
        $disabled={disabled}
        $backgroundColor={backgroundColor}
      >
        <span className="spinnerContainer" data-fs={name}>
          <Spinner $spinnerColor={spinnerColor} size={25} className="spinner" />
        </span>
        <p className="medium bold text">{text}</p>
      </StyledButton>
    </StyledButtonContainer>
  );
};

Button.propTypes = {
  text: PropTypes.string,
  onClick: PropTypes.func,
  backgroundColor: PropTypes.string,
  inverted: PropTypes.bool,
  disabled: PropTypes.bool,
};

export { Button };
